import katex from 'katex';

import { interpolateValues } from './interpolate-values';

const latexCache = new Map<string, string>();
const MAX_LATEX_CACHE = 500;

/**
 * Render `$...$` delimited LaTeX segments via KaTeX.
 * Segments not wrapped in `$` are passed through unchanged.
 * Results are cached keyed on raw text to avoid re-rendering identical strings.
 */
export function renderLatex(text: string): string {
  if (!text.includes('$')) {
    return text;
  }

  const cached = latexCache.get(text);
  if (cached !== undefined) {
    return cached;
  }

  const rendered = text.replace(/(?<!\\)\$(.+?)(?<!\\)\$/g, (_, expr: string) =>
    katex.renderToString(expr, { throwOnError: false, output: 'html' }),
  );

  if (latexCache.size >= MAX_LATEX_CACHE) {
    latexCache.delete(latexCache.keys().next().value!);
  }
  latexCache.set(text, rendered);
  return rendered;
}

/**
 * Convert literal `\n` sequences in text to `<br>` for HTML rendering.
 * Applied after all template interpolation.
 */
export function convertLineBreaks(text: string): string {
  return text.replace(/\n/g, '<br>');
}

/**
 * Apply the full rich-text pipeline: interpolate values, render LaTeX, convert line breaks.
 * This is the single entry point for controls.
 *
 * Processing order matters:
 * 1. Value interpolation first (so `{path}` resolves before LaTeX processing)
 * 2. LaTeX rendering (produces HTML from `$...$`)
 * 3. Line break conversion (`\n` → `<br>`)
 */
export function processRichText(
  text: string,
  resolveValue?: (path: string) => number | string | undefined,
  precision?: number,
): string {
  let result = text;
  if (resolveValue) {
    result = interpolateValues(result, resolveValue, precision);
  }
  result = renderLatex(result);
  result = convertLineBreaks(result);
  return result;
}
